/**
 * Create and initialise Tako tooltips
 */

/* -----------------------------------------------------------------------------
 * Tako Tooltip
 * -------------------------------------------------------------------------- */

import { getIsRtl } from './utils/getIsRtl';
import { takoUUID } from './utils/takoUUID';
import { getLogger } from './utils/logger-context';
import { resolveComponentScope } from './utils/resolveComponentScope';

const COMPONENT_NAME = 'Tooltip';

export const init = (unresolvedScope) => {
    const logger = getLogger();
    const scope = resolveComponentScope(unresolvedScope);
    const defaultLoggerArgs = {
        componentName: COMPONENT_NAME,
        scope,
    };
    logger.debug(`Initialising ${COMPONENT_NAME} component.`, { scope });

    const allTakoTooltips = scope.querySelectorAll(
        '[data-tooltip-content], [data-inline-tooltip-content], [data-tooltip-dangerously-set-inner-html], [data-dangerously-set-inner-html], .tako-tooltip',
    );

    allTakoTooltips.forEach((takoTooltip) => {
        try {
            warnOnDeprecatedClasses(takoTooltip);

            takoTooltip.classList.add('tako-tooltip');
            takoTooltip.setAttribute('tabindex', '0');
            takoTooltip.setAttribute('role', 'button');
            if (takoTooltip.hasAttribute('data-inline-tooltip-content')) {
                takoTooltip.classList.add('tako-tooltip--text');
            }

            const { tooltipBody, tooltipBodyId } =
                getTooltipContent(takoTooltip);
            if (!tooltipBody) {
                return;
            } else {
                /**
                 * if tooltip is part of a modal
                 * - add an attribute to change z-index
                 * - move the tooltip body outside the modal
                 */
                const modalParent = takoTooltip.closest('.tako-modal');
                if (modalParent) {
                    tooltipBody.setAttribute('data-modal-content', true);
                    modalParent.insertAdjacentElement('afterend', tooltipBody);
                } else {
                    takoTooltip.insertAdjacentElement('afterend', tooltipBody);
                }
                takoTooltip.setAttribute('aria-describedby', tooltipBodyId);
            }

            const lazy = takoTooltip.getAttribute('data-lazy');
            if (lazy !== null && lazy.toLowerCase() === 'true') {
                const initEvents = ['mouseenter', 'focus'];
                initEvents.forEach((event) => {
                    function lazyInit() {
                        initiatePopper(takoTooltip, tooltipBody, true);
                        takoTooltip.removeAttribute('data-lazy');
                        takoTooltip.removeEventListener(event, lazyInit);
                    }

                    takoTooltip.addEventListener(event, lazyInit);
                });
            } else {
                initiatePopper(takoTooltip, tooltipBody);
            }
        } catch (error) {
            logger.error(error, {
                ...defaultLoggerArgs,
                element: takoTooltip,
            });
        }
    });
};

function warnOnDeprecatedClasses(takoTooltip) {
    const logger = getLogger();

    // TODO: Remove with the next breaking change
    if (takoTooltip.classList.contains('tako-tooltip')) {
        logger.warn(
            'The `tako-tooltip` class is deprecated. Please use the `data-tooltip-content` attribute instead.',
        );
    }
    // TODO: Remove with the next breaking change
    if (takoTooltip.classList.contains('tako-tooltip--text')) {
        logger.warn(
            'The `tako-tooltip--text` class is deprecated. Please use the `data-inline-tooltip-content` attribute instead.',
        );
    }
}

function getTooltipContent(takoTooltip) {
    const logger = getLogger();
    const tooltipBodyId = takoUUID('tooltip');

    // Generate tooltip body and insert after trigger element
    const tooltipBody = document.createElement('div');
    tooltipBody.classList.add('tako-tooltip__body');
    tooltipBody.setAttribute('role', 'tooltip');
    tooltipBody.setAttribute('id', tooltipBodyId);
    // Hidden by default for accessibility - shown/hidden programmatically
    tooltipBody.setAttribute('hidden', '');

    // Live region wrapper so screen readers announce tooltip content when shown
    const tooltipContent = document.createElement('span');
    tooltipContent.setAttribute('role', 'status');

    // Check that we do not pass arbitrary strings into the DOM which might lead to code execution.
    // Previously it was only occasionally used to do this on
    // purpose but did enable accidental code execution by e.g.
    // passing user data into the DOM.
    // Only the `data-content` attribute should be used for text display.
    // If you have no other option (and you tried really hard) but
    // you have to display custom HTML as part of the tooltip then
    // use the `data-unsafe-html` attribute.
    let textContent =
        takoTooltip.getAttribute('data-tooltip-content') ||
        takoTooltip.getAttribute('data-inline-tooltip-content');

    // TODO: Remove conditional with the next breaking change
    if (!textContent && takoTooltip.getAttribute('data-content')) {
        logger.warn(
            "Tako tooltip's `data-content` attribute is deprecated. Please use `data-tooltip-content` instead.",
        );
        textContent = takoTooltip.getAttribute('data-content');
    }

    let dangerousContent = takoTooltip.getAttribute(
        'data-tooltip-dangerously-set-inner-html',
    );

    // TODO: Remove conditional with the next breaking change
    if (
        !dangerousContent &&
        takoTooltip.getAttribute('data-dangerously-set-inner-html')
    ) {
        logger.warn(
            "Tako tooltip's `data-dangerously-set-inner-html` attribute is deprecated. Please use `data-tooltip-dangerously-set-inner-html` instead.",
        );
        dangerousContent = takoTooltip.getAttribute(
            'data-dangerously-set-inner-html',
        );
    }

    if (!textContent && !dangerousContent) {
        logger.error(
            'Tako Tooltip has not been provided with content. Please provide a `string` value to the `data-content` attribute. Omitting component...',
        );
        return null;
    }

    if (textContent && dangerousContent) {
        logger.warn(
            "Tako Tooltip content has been set in both `data-content` and `data-dangerously-set-inner-html` attributes. Attribute `data-content` will be used since it's safer...",
        );
    }

    if (textContent) {
        if (typeof textContent !== 'string') {
            throw "Tako Tooltip's `data-content` attribute should be provided with a `string`. Omitting component...";
        }
        tooltipContent.innerText = textContent;
    } else if (dangerousContent) {
        logger.warn(
            "Tako Tooltip's `data-dangerously-set-inner-html` has been used. For security reasons, please avoid using this attribute unless absolutely necessary",
        );
        tooltipContent.innerHTML = dangerousContent;
    }

    tooltipBody.appendChild(tooltipContent);
    setTheme(takoTooltip, tooltipBody);
    tooltipBody.appendChild(getTooltipBodyArrow());

    return { tooltipBody, tooltipBodyId };
}
function getTooltipBodyArrow() {
    const tooltipArrow = document.createElement('div');
    tooltipArrow.classList.add('tako-tooltip__arrow');
    tooltipArrow.setAttribute('data-popper-arrow', true);
    return tooltipArrow;
}
function setTheme(takoTooltip, tooltipBody) {
    const logger = getLogger();

    // TODO: Remove light theme with the next breaking change
    const lightTheme = takoTooltip.classList.contains('tako-tooltip--light');
    if (lightTheme) {
        tooltipBody.setAttribute('data-theme', 'light');
        logger.warn(
            'The Tako tooltip light theme is deprecated. Following the next breaking change all tooltips will have the default theme.',
        );
    }
}

function initiatePopper(takoTooltip, tooltipBody, forceShow = false) {
    const options = {
        modifiers: [{ name: 'offset', options: { offset: [0, 8] } }],
        placement: getPlacement(takoTooltip),
    };

    // eslint-disable-next-line no-undef
    const popperTooltipInstance = Popper.createPopper(
        takoTooltip,
        tooltipBody,
        options,
    );

    showHide(tooltipBody, popperTooltipInstance, takoTooltip, forceShow);
}
function getPlacement(takoTooltip) {
    const placementAttr = takoTooltip.getAttribute('data-placement');

    const placementOptions = ['top', 'right', 'end', 'bottom', 'left', 'start'];
    if (!placementOptions.includes(placementAttr)) {
        return 'top';
    }

    if (placementAttr === 'end') {
        return getIsRtl() ? 'left' : 'right';
    } else if (placementAttr === 'start') {
        return getIsRtl() ? 'right' : 'left';
    }

    return placementAttr;
}
function showHide(tooltipBody, popperTooltipInstance, takoTooltip, forceShow) {
    // -------------------------------------------------------------------------
    // Popper.js utilities
    // -------------------------------------------------------------------------

    function merge(originalObject, overrides) {
        return Object.values(
            []
                .concat(originalObject, overrides)
                .reduce(
                    (properties, currentProperty) => (
                        (properties[currentProperty.name] = Object.assign(
                            properties[currentProperty.name] || {},
                            currentProperty,
                        )),
                        properties
                    ),
                    {},
                ),
        );
    }

    function setPopperToolTipOptions(newModifier) {
        popperTooltipInstance.setOptions((options) =>
            Object.assign(options, {
                modifiers: merge(options.modifiers, newModifier),
            }),
        );
    }

    // -------------------------------------------------------------------------
    // Timeout management
    // -------------------------------------------------------------------------

    const HIDE_DELAY_MS = 100;
    let hideTimeout = null;

    function cancelHideTimeout() {
        if (hideTimeout) {
            clearTimeout(hideTimeout);
            hideTimeout = null;
        }
    }

    // -------------------------------------------------------------------------
    // Tooltip visibility
    // -------------------------------------------------------------------------

    function showTooltip() {
        cancelHideTimeout();
        tooltipBody.removeAttribute('hidden');
        tooltipBody.setAttribute('data-show', true);
        setPopperToolTipOptions([{ name: 'eventListeners', enabled: true }]);
        popperTooltipInstance.update();
    }

    function hideTooltip() {
        cancelHideTimeout();
        tooltipBody.setAttribute('hidden', '');
        tooltipBody.removeAttribute('data-show');
        setPopperToolTipOptions([{ name: 'eventListeners', enabled: false }]);
    }

    function hideTooltipWithDelay() {
        hideTimeout = setTimeout(hideTooltip, HIDE_DELAY_MS);
    }

    // -------------------------------------------------------------------------
    // Event bindings: trigger element (show on hover/focus, hide on leave/blur)
    // -------------------------------------------------------------------------

    ['mouseenter', 'focus'].forEach((event) =>
        takoTooltip.addEventListener(event, showTooltip),
    );

    ['mouseleave', 'blur'].forEach((event) =>
        takoTooltip.addEventListener(event, hideTooltipWithDelay),
    );

    // -------------------------------------------------------------------------
    // Event bindings: tooltip body (keep visible while user hovers over it)
    // -------------------------------------------------------------------------

    tooltipBody.addEventListener('mouseenter', cancelHideTimeout);
    tooltipBody.addEventListener('mouseleave', hideTooltipWithDelay);

    // -------------------------------------------------------------------------
    // Keyboard accessibility: dismiss tooltip with Escape key
    // -------------------------------------------------------------------------

    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && tooltipBody.hasAttribute('data-show')) {
            hideTooltip();
        }
    });

    // -------------------------------------------------------------------------
    // Initial state
    // -------------------------------------------------------------------------

    if (forceShow) {
        showTooltip();
    }
}
